import { Button, Checkbox, Chip, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import DeleteModal from "components/Modals/DeleteModal";
import RestoreModal from "components/Modals/RestoreModal";
import { eventClick, ImageName, RefreshData } from "components/helper";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import DisplayDate from "components/DisplayDate";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit, AiFillEye } from "react-icons/ai";
import { FaPlusCircle } from "react-icons/fa";
import { MdRestore } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  canCreatePlatformOrganisation,
  canManagePlatformOrganisations,
  hasPermissionKey,
  isPlatformAdmin,
} from "utils/permissionUtils";

const getCreatedDate = (row) => row?.createdAt ?? row?.createdDate;
const getUpdatedDate = (row) => row?.updatedAt ?? row?.updatedDate;

const Organisation = () => {
  const navigate = useNavigate();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedList, setDeletedList] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreModal, setRestoreModal] = useState({ open: false, id: null, name: null });

  const [{ consultancyList, token, isConsultancyListLoading }, { refetchConsultancy }] =
    useKothar();
  const superAdmin = isPlatformAdmin();
  const canManageOrgs = canManagePlatformOrganisations();
  const canCreateOrg = canCreatePlatformOrganisation();

  const fetchDeletedOrganisations = useCallback(async () => {
    if (!canManageOrgs || !token) return;
    setDeletedLoading(true);
    try {
      const res = await axios.get(`${API_URL}/organization/deleted`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeletedList(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Failed to load deleted organisations");
      setDeletedList([]);
    } finally {
      setDeletedLoading(false);
    }
  }, [canManageOrgs, token]);

  useEffect(() => {
    if (!consultancyList?.length) {
      refetchConsultancy();
    }
  }, []);

  useEffect(() => {
    if (showDeleted && canManageOrgs) {
      fetchDeletedOrganisations();
    }
  }, [showDeleted, canManageOrgs, fetchDeletedOrganisations]);

  useEffect(() => {
    const orgId = openConfirmationModal?.id;
    if (!openConfirmationModal.state || !permanentDelete || !superAdmin || !orgId) {
      setDeletePreview(null);
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    axios
      .get(`${API_URL}/organization/${orgId}/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setDeletePreview(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err?.response?.data?.errorMessage || "Failed to load delete preview");
          setPermanentDelete(false);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openConfirmationModal.state, openConfirmationModal.id, permanentDelete, superAdmin, token]);

  const closeModal = () => {
    setOpenConfirmationModal({ state: false, id: null });
    setPermanentDelete(false);
    setDeletePreview(null);
  };

  const refreshLists = () => {
    refetchConsultancy();
    if (showDeleted) fetchDeletedOrganisations();
  };

  const handleDeleteData = () => {
    const orgId = openConfirmationModal?.id;
    const url = permanentDelete
      ? `${API_URL}/organization/${orgId}/permanent`
      : `${API_URL}/organization/${orgId}`;

    deleteData(
      url,
      () => {
        closeModal();
        refreshLists();
      },
      token
    );
  };

  const handleRestore = async () => {
    const orgId = restoreModal.id;
    if (!orgId) return;
    setRestoringId(orgId);
    try {
      await axios.post(
        `${API_URL}/organization/${orgId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Organisation restored successfully");
      setRestoreModal({ open: false, id: null, name: null });
      refreshLists();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Failed to restore organisation");
    } finally {
      setRestoringId(null);
    }
  };

  const activeColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 200,
        Cell: ({ row, renderedCellValue }) => (
          <div className="flex items-center">
            {ImageName(row.original?.name)}
            <span className="ml-3 font-bold text-app-text-secondary">
              {renderedCellValue || "-"}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 180,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "owner",
        header: "Owner",
        size: 150,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "primaryContactNumber",
        header: "Contact",
        size: 140,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "website",
        header: "Website",
        size: 150,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "abn",
        header: "PAN/ABN",
        size: 120,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        id: "createdDate",
        header: "Created Date",
        size: 140,
        accessorFn: (row) => getCreatedDate(row),
        Cell: ({ row }) => <DisplayDate value={getCreatedDate(row.original)} />,
      },
      {
        id: "updatedDate",
        header: "Last Updated",
        size: 140,
        accessorFn: (row) => getUpdatedDate(row),
        Cell: ({ row }) => <DisplayDate value={getUpdatedDate(row.original)} />,
      },
      {
        id: "actions",
        header: "Actions",
        size: 150,
        enableSorting: false,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              <Tooltip title="View" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/admin/consultancy/view/${item?.id}`, {
                      state: { item },
                    })
                  }
                >
                  <AiFillEye className="text-primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Organisation" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate("/admin/consultancy/add", {
                      state: { item },
                    })
                  }
                  disabled={!hasPermissionKey(P.CONSULTANCY.UPDATE)}
                >
                  <AiFillEdit className="text-primary" />
                </IconButton>
              </Tooltip>
              {canManageOrgs && (
                <Tooltip title="Soft delete organisation" arrow>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setOpenConfirmationModal({
                        state: true,
                        id: item?.id,
                        name: item?.name,
                      })
                    }
                  >
                    <AiFillDelete className="text-red-600" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ],
    [canManageOrgs, navigate]
  );

  const deletedColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 200,
        Cell: ({ row, renderedCellValue }) => (
          <div className="flex items-center">
            {ImageName(row.original?.name)}
            <span className="ml-3 font-bold text-app-text-secondary">
              {renderedCellValue || "-"}
            </span>
            <Chip
              label="Deleted"
              size="small"
              color="default"
              className="ml-2"
              variant="outlined"
            />
          </div>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 180,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "owner",
        header: "Owner",
        size: 150,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "primaryContactNumber",
        header: "Contact",
        size: 140,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "website",
        header: "Website",
        size: 150,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "abn",
        header: "PAN/ABN",
        size: 120,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        id: "createdDate",
        header: "Created Date",
        size: 140,
        accessorFn: (row) => getCreatedDate(row),
        Cell: ({ row }) => <DisplayDate value={getCreatedDate(row.original)} />,
      },
      {
        accessorKey: "deletedAt",
        header: "Deleted Date",
        size: 140,
        Cell: ({ renderedCellValue }) => <DisplayDate value={renderedCellValue} />,
      },
      {
        id: "actions",
        header: "Actions",
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            canManageOrgs && (
              <Tooltip title="Restore organisation" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setRestoreModal({ open: true, id: item?.id, name: item?.name })}
                    disabled={restoringId === item?.id}
                  >
                    <MdRestore className="text-primary" />
                  </IconButton>
                </span>
              </Tooltip>
            )
          );
        },
      },
    ],
    [canManageOrgs, restoringId]
  );

  const activeTable = useMaterialReactTable({
    columns: activeColumns,
    data: consultancyList || [],
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      sorting: [{ id: "name", desc: false }],
      pagination: { pageSize: 20, pageIndex: 0 },
    },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: showDeleted ? "50vh" : "70vh",
        minHeight: showDeleted ? "50vh" : "70vh",
        overflowY: "auto",
      },
    },
    state: {
      isLoading: isConsultancyListLoading,
      showProgressBars: isConsultancyListLoading,
      isSaving: isConsultancyListLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (!eventClick(event)) {
          navigate(`/admin/consultancy/view/${row?.original?.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  const deletedTable = useMaterialReactTable({
    columns: deletedColumns,
    data: deletedList,
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      sorting: [{ id: "name", desc: false }],
      pagination: { pageSize: 20, pageIndex: 0 },
    },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "50vh",
        minHeight: "40vh",
        overflowY: "auto",
      },
    },
    state: {
      isLoading: deletedLoading,
      showProgressBars: deletedLoading,
      isSaving: deletedLoading,
    },
    muiTableBodyRowProps: {
      sx: { opacity: 0.8 },
    },
  });

  const orgPreviewWarnings = deletePreview?.warnings || [];

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className={"font-semibold text-lg " + "text-app-text"}>
                Organisations ({consultancyList?.length || 0}){" "}
                <RefreshData refreshData={refetchConsultancy} />
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                {canManageOrgs && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showDeleted}
                        onChange={(e) => setShowDeleted(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Show deleted"
                  />
                )}
                {canCreateOrg && (
                  <Button
                    variant="contained"
                    startIcon={<FaPlusCircle />}
                    component={Link}
                    to="/admin/consultancy/add"
                  >
                    Add Organisation
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={activeTable} />
          </div>
        </div>

        {showDeleted && canManageOrgs && (
          <div
            className={
              "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
              "bg-app-surface text-app-text border border-app-border"
            }
          >
            <div className="rounded-t mb-0 px-4 py-3 border-0 border-b border-app-border">
              <h3 className="font-semibold text-lg text-app-text">
                Deleted organisations ({deletedList?.length || 0})
              </h3>
              <p className="text-sm text-app-text-secondary mt-1">
                Soft-deleted organisations can be restored. Permanent deletes cannot.
              </p>
            </div>
            <div className="block w-full overflow-x-auto">
              <MaterialReactTable table={deletedTable} />
            </div>
          </div>
        )}
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item={permanentDelete ? "Organisation permanently" : "Organisation"}
          bodyText={
            permanentDelete
              ? `Permanently delete ${
                  openConfirmationModal?.name || "this organisation"
                } and all related data. This cannot be undone.`
              : `Remove ${
                  openConfirmationModal?.name || "this organisation"
                } from lists? It will be soft-deleted and can be restored later.`
          }
          handleCancel={closeModal}
          handleDelete={handleDeleteData}
          previewLoading={previewLoading}
          warningItems={
            permanentDelete
              ? orgPreviewWarnings
              : [
                  "The organisation will be removed from lists and API responses.",
                  "Data is kept in the database and can be restored from the deleted list.",
                  "Use permanent delete (super admin only) to remove all related data from the database.",
                ]
          }
          extraContent={
            superAdmin ? (
              <div className="mb-4 rounded-lg border border-app-border bg-app-surface-alt p-3">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permanentDelete}
                      onChange={(e) => setPermanentDelete(e.target.checked)}
                      color="error"
                    />
                  }
                  label="Permanently delete organisation and all related data (super admin)"
                />
                {permanentDelete && deletePreview && (
                  <p className="text-sm text-app-text-secondary mt-1 ml-8">
                    {deletePreview.organizationName}: {deletePreview.clientsCount} client(s),{" "}
                    {deletePreview.studentsCount} admission(s), {deletePreview.usersCount} user(s).
                  </p>
                )}
              </div>
            ) : null
          }
        />
      )}
      <RestoreModal
        open={restoreModal.open}
        itemName={restoreModal.name}
        handleCancel={() => setRestoreModal({ open: false, id: null, name: null })}
        handleRestore={handleRestore}
        loading={restoringId === restoreModal.id}
      />
    </div>
  );
};

export default Organisation;
